import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';

export const THEME_PRESETS = ['light', 'dark', 'oled'] as const;
export type ThemePreset = (typeof THEME_PRESETS)[number];

export const PRESET_LABELS: Record<ThemePreset, string> = { light: 'Açık', dark: 'Koyu', oled: 'OLED' };

/** Backend'deki tenant teması (GET /branding → theme). */
export interface BrandTheme {
  presets: ThemePreset[];
  defaultPreset: ThemePreset;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  radius?: number;
  fontFamily?: string;
}

interface ThemeContextValue {
  preset: ThemePreset;
  available: ThemePreset[];
}

const ThemeContext = createContext<ThemeContextValue>({ preset: 'light', available: ['light'] });

/**
 * Hazır temayı <html data-theme> ile, marka renklerini --brand-* değişkenleriyle uygular.
 * `preset` açık temalar arasında değilse varsayılana düşülür.
 */
export function ThemeProvider({ brand, preset, children }: { brand?: BrandTheme | null; preset?: ThemePreset | null; children: ReactNode }) {
  const available = brand?.presets?.length ? brand.presets : (['light', 'dark'] as ThemePreset[]);
  const effective: ThemePreset = preset && available.includes(preset) ? preset : (brand?.defaultPreset ?? available[0] ?? 'light');

  useEffect(() => {
    document.documentElement.dataset.theme = effective;
  }, [effective]);

  useEffect(() => {
    const root = document.documentElement.style;
    const vars: Record<string, string | undefined> = {
      '--brand-primary': brand?.primaryColor,
      '--brand-secondary': brand?.secondaryColor,
      '--brand-accent': brand?.accentColor,
      '--brand-radius': brand?.radius !== undefined ? `${brand.radius}px` : undefined,
      '--brand-font': brand?.fontFamily ? `${brand.fontFamily}, ui-sans-serif, system-ui, sans-serif` : undefined,
    };
    for (const [k, v] of Object.entries(vars)) {
      if (v) root.setProperty(k, v);
      else root.removeProperty(k);
    }
  }, [brand?.primaryColor, brand?.secondaryColor, brand?.accentColor, brand?.radius, brand?.fontFamily]);

  const value = useMemo(() => ({ preset: effective, available }), [effective, available]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

/** Tarayıcı meta theme-color ve favicon güncellemesi (markalama). */
export function useDocumentBranding(opts: { title?: string | null; faviconUrl?: string | null }) {
  useEffect(() => {
    if (opts.title) document.title = opts.title;
  }, [opts.title]);
  useEffect(() => {
    if (!opts.faviconUrl) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = opts.faviconUrl;
  }, [opts.faviconUrl]);
}
